import React, { useEffect, useState, useRef, useCallback } from "react";
import { Outlet, Link } from "react-router-dom";
import { Bell, X, CheckCheck, ShieldCheck, Scale, Star, AlertTriangle, Menu, MessageCircle } from "lucide-react";
import Sidebar from "./Sidebar";
import ChatbotWidget from "./ChatbotWidget";
import ChatPanel from "./ChatPanel";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Toaster } from "../components/ui/sonner";
import { useEditModeSession } from "../hooks/useEditModeSession";

const LANGS = [
  { value: "en", label: "English" },
  { value: "hi", label: "हिंदी" },
  { value: "mr", label: "मराठी" },
  { value: "pa", label: "ਪੰਜਾਬੀ" },
  { value: "kn", label: "ಕನ್ನಡ" },
  { value: "gu", label: "ગુજરાતી" },
];

// SlotsBadge removed — subscription model hidden (unlimited agreements enabled)

function KycBanner({ user }) {
  if (!user || user.kyc_status === "verified") return null;

  const messages = {
    tenant: "Complete your KYC to sign agreements and protect your deposit. Takes 2 minutes.",
    landlord: "Complete your KYC to sign agreements and start renting with confidence. Takes 2 minutes.",
    agent: "Complete your KYC to send agreements to your clients. Takes 2 minutes.",
    broker_admin: "Complete your KYC to send agreements to your clients. Takes 2 minutes.",
  };
  const text = messages[user.role] || messages.tenant;

  return (
    <div style={{
      background: "#F97316",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      padding: "10px 24px",
      fontSize: 13,
      fontWeight: 500,
      flexShrink: 0,
    }}>
      <AlertTriangle style={{ height: 15, width: 15, flexShrink: 0 }} />
      <span>⚠️ {text}</span>
      <Link
        to="/onboarding/kyc"
        style={{
          color: "#fff",
          fontWeight: 700,
          textDecoration: "underline",
          whiteSpace: "nowrap",
          marginLeft: 4,
        }}
      >
        Complete KYC →
      </Link>
    </div>
  );
}

function NotificationIcon(type) {
  const icons = {
    new_message: <span>💬</span>,
    agreement_signed: <ShieldCheck className="h-4 w-4 text-emerald-500" />,
    deposit_paid: <span>💰</span>,
    movein_locked: <span>🔒</span>,
    deduction_proposed: <span>📋</span>,
    dispute_raised: <Scale className="h-4 w-4 text-red-500" />,
    settlement_done: <Star className="h-4 w-4 text-[#F97316]" />,
    default: <Bell className="h-4 w-4 text-gray-400" />,
  };
  return icons[type] || icons.default;
}

function NotificationDropdown({ open, onClose, user, isMobile = false }) {
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);

  const statusMessage = useCallback((status) => {
    const map = {
      agreement_pending: "Agreement pending signatures",
      active: "Tenancy is active",
      checkout_pending: "Move-out report pending",
      settlement_pending: "Settlement awaiting approval",
      completed: "Deal completed ✓",
      dispute_review: "Dispute under Orello review",
      external_dispute: "External dispute in progress",
    };
    return map[status] || status;
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/notifications", {
        headers: { Authorization: `Bearer ${localStorage.getItem("orello_token")}` },
      });
      const data = await res.json();
      setNotifs(Array.isArray(data) ? data : []);
    } catch (e) {
      // generate from rentals if no notifications endpoint
      try {
        const res2 = await fetch("/api/rentals", {
          headers: { Authorization: `Bearer ${localStorage.getItem("orello_token")}` },
        });
        const rentals = await res2.json();
        // Build status notifications from rentals
        const generated = (rentals || []).slice(0, 10).map(r => ({
          id: r.id,
          rental_id: r.id,
          type: "status_update",
          title: r.property_address,
          body: statusMessage(r.status),
          read: true,
          created_at: r.created_at,
        }));
        setNotifs(generated);
      } catch {}
    } finally {
      setLoading(false);
    }
  }, [statusMessage]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, onClose]);

  const markAllRead = async () => {
    try {
      await fetch("/api/notifications/read-all", {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("orello_token")}` },
      });
      setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    } catch {}
  };

  if (!open) return null;

  const unread = notifs.filter(n => !n.read).length;
  const dropdownWidth = isMobile ? "calc(100vw - 24px)" : 384;
  const dropdownStyle = isMobile
    ? {
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        width: "100%",
        maxWidth: dropdownWidth,
        maxHeight: "80vh",
        borderRadius: "16px 16px 0 0",
        background: "var(--dash-card)",
        border: `1px solid var(--dash-border)`,
        zIndex: 50,
        overflow: "hidden",
      }
    : {
        position: "absolute",
        right: 0,
        top: 48,
        width: dropdownWidth,
        background: "var(--dash-card)",
        borderRadius: 16,
        boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        border: "1px solid var(--dash-border)",
        zIndex: 50,
        overflow: "hidden",
      };

  return (
    <div ref={ref} style={dropdownStyle}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">Notifications</h3>
          {unread > 0 && (
            <span className="bg-[#F97316] text-white text-xs font-bold px-2 py-0.5 rounded-full">{unread}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-[#F97316] hover:underline flex items-center gap-1">
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-4 w-4 text-gray-400" />
          </button>
        </div>
      </div>

      {/* List */}
      <div className="max-h-[420px] overflow-y-auto">
        {loading && (
          <div className="p-8 text-center text-sm text-gray-400">Loading...</div>
        )}
        {!loading && notifs.length === 0 && (
          <div className="p-8 text-center">
            <Bell className="h-8 w-8 text-gray-200 mx-auto mb-2" />
            <p className="text-sm text-gray-400">No notifications yet</p>
            <p className="text-xs text-gray-300 mt-1">Deal updates will appear here</p>
          </div>
        )}
        {!loading && notifs.map((n, i) => (
          <Link
            key={n.id || i}
            to={n.rental_id ? `/app/rentals/${n.rental_id}` : "/app"}
            onClick={onClose}
            className={`flex items-start gap-3 px-5 py-4 hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 ${!n.read ? "bg-orange-50/40" : ""}`}
          >
            <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
              {NotificationIcon(n.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className={`text-sm leading-snug ${!n.read ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                  {n.title}
                </p>
                {!n.read && <div className="h-2 w-2 rounded-full bg-[#F97316] flex-shrink-0 mt-1" />}
              </div>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{n.body}</p>
              {n.created_at && (
                <p className="text-[10px] text-gray-300 mt-1">
                  {new Date(n.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-gray-100 bg-gray-50">
        <Link to="/app" onClick={onClose} className="text-xs text-[#F97316] hover:underline">
          View all deals →
        </Link>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user } = useAuth();
  const { lang, setLang } = useI18n();
  useEditModeSession(); // Initialize edit mode session with mock user if in edit mode
  const [notifOpen, setNotifOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    if (!user) return;
    // Poll unread count every 30s
    const check = async () => {
      try {
        const res = await fetch("/api/notifications", {
          headers: { Authorization: `Bearer ${localStorage.getItem("orello_token")}` },
        });
        const data = await res.json();
        if (Array.isArray(data)) {
          setUnreadCount(data.filter(n => !n.read).length);
        }
      } catch {}
    };
    check();
    const interval = setInterval(check, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "var(--dash-bg)", color: "var(--dash-text)" }}>
      {/* Sidebar - Hidden on mobile, visible on desktop (collapsible) */}
      {!isMobile && <Sidebar collapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} />}

      {/* Mobile sidebar overlay */}
      {isMobile && sidebarOpen && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 40,
              top: 56,
            }}
            onClick={() => setSidebarOpen(false)}
          />
          <div style={{ position: "fixed", top: 56, left: 0, bottom: 0, width: 240, zIndex: 41, overflowY: "auto" }}>
            <Sidebar onClose={() => setSidebarOpen(false)} />
          </div>
        </>
      )}

      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <header
          data-testid="dashboard-header"
          style={{
            height: 56, background: "var(--dash-header)",
            borderBottom: "1px solid var(--dash-border)",
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: isMobile ? "0 12px" : "0 24px", position: "sticky", top: 0, zIndex: 30,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12 }}>
            {isMobile && (
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                style={{ padding: 8, borderRadius: 8, background: "transparent", border: "none", cursor: "pointer", color: "var(--dash-text-2)" }}
              >
                <Menu style={{ height: 20, width: 20 }} />
              </button>
            )}
            <div style={{ position: "relative" }}>
              <button
                onClick={() => setNotifOpen(o => !o)}
                data-testid="notif-bell"
                style={{ position: "relative", padding: 8, borderRadius: 8, background: "transparent", border: "none", cursor: "pointer", color: "var(--dash-text-2)" }}
              >
                <Bell style={{ height: 20, width: 20 }} />
                {unreadCount > 0 && (
                  <span style={{ position: "absolute", top: 4, right: 4, height: 16, width: 16, background: "#F97316", borderRadius: "50%", color: "#fff", fontSize: 10, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              <NotificationDropdown open={notifOpen} onClose={() => setNotifOpen(false)} user={user} isMobile={isMobile} />
            </div>
            <button
              onClick={() => setChatOpen(!chatOpen)}
              style={{ padding: 8, borderRadius: 8, background: "transparent", border: "none", cursor: "pointer", color: "var(--dash-text-2)" }}
              title="WhatsApp Messages"
            >
              <MessageCircle style={{ height: 20, width: 20 }} />
            </button>
            <select
              data-testid="lang-switcher"
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              aria-label="Select language"
              style={{
                background: "var(--dash-input, #f9fafb)",
                border: "1px solid var(--dash-border, #e5e7eb)",
                borderRadius: 8,
                color: "var(--dash-text, #111)",
                fontSize: 12,
                fontWeight: 500,
                padding: "5px 24px 5px 8px",
                cursor: "pointer",
                outline: "none",
                appearance: "none",
                WebkitAppearance: "none",
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%236B7280'/%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 7px center",
                minWidth: isMobile ? 60 : 90,
              }}
            >
              {LANGS.map((l) => (
                <option key={l.value} value={l.value}>{l.label}</option>
              ))}
            </select>
            <div data-testid="avatar" style={{ height: 32, width: 32, borderRadius: "50%", background: "linear-gradient(135deg, #F97316 0%, #8B5CF6 100%)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600, fontSize: 14 }}>
              {user?.name?.[0]?.toUpperCase()}
            </div>
          </div>
        </header>
        <KycBanner user={user} />
        <main style={{ flex: 1, padding: isMobile ? "16px 12px" : "24px", maxWidth: 1100, width: "100%", margin: "0 auto" }}>
          <Outlet />
        </main>
      </div>
      <ChatPanel isOpen={chatOpen} onClose={() => setChatOpen(false)} />
      <Toaster richColors position={isMobile ? "bottom-right" : "top-right"} />
      <ChatbotWidget />
    </div>
  );
}